import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Keyboard } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import { lockScroll, unlockScroll } from '../services/scrollLock';

import 'swiper/css';

type ImageLightboxProps = {
  isOpen: boolean;
  images: string[];
  startIndex: number;
  onClose: () => void;
  label?: string;
};

const ArrowIcon = ({ direction }: { direction: 'left' | 'right' }) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    className={`h-5 w-5 ${direction === 'right' ? 'rotate-180' : ''}`}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

export const ImageLightbox: React.FC<ImageLightboxProps> = ({ isOpen, images, startIndex, onClose, label }) => {
  const swiperRef = useRef<SwiperType | null>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    lockScroll();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('modal-opened'));
    }
    return () => {
      unlockScroll();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('modal-closed'));
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !swiperRef.current) return;
    swiperRef.current.slideTo(startIndex, 0);
  }, [isOpen, startIndex]);

  if (!isOpen || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="group fixed inset-0 z-[200] flex items-center justify-center bg-black/90 backdrop-blur-xl"
      role="dialog"
      aria-modal="true"
      aria-label={label || 'Image lightbox'}
      onClick={onClose}
    >
      <div
        className="relative flex h-full w-full items-center justify-center px-4 py-10 sm:px-10"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 z-20 rounded-full border border-white/15 bg-black/40 p-2 text-zinc-100 shadow-lg transition-colors hover:bg-white/15 hover:text-white"
          aria-label="Close lightbox"
        >
          <X size={16} />
        </button>

        {images.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => swiperRef.current?.slidePrev()}
              className="absolute left-2 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/30 text-white opacity-0 transition-all hover:bg-black/50 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto sm:flex"
              aria-label="Previous image"
            >
              <ArrowIcon direction="left" />
            </button>
            <button
              type="button"
              onClick={() => swiperRef.current?.slideNext()}
              className="absolute right-2 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/30 text-white opacity-0 transition-all hover:bg-black/50 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto sm:flex"
              aria-label="Next image"
            >
              <ArrowIcon direction="right" />
            </button>
          </>
        )}

        <Swiper
          className="lightbox-swiper h-full w-full"
          modules={[Keyboard]}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          onTouchStart={(_, event) => {
            if (!('touches' in event) || event.touches.length === 0) return;
            const touch = event.touches[0];
            touchStartRef.current = { x: touch.clientX, y: touch.clientY };
          }}
          onTouchEnd={(_, event) => {
            if (!touchStartRef.current) return;
            if (!('changedTouches' in event) || event.changedTouches.length === 0) {
              touchStartRef.current = null;
              return;
            }
            const touch = event.changedTouches[0];
            const deltaX = touch.clientX - touchStartRef.current.x;
            const deltaY = touch.clientY - touchStartRef.current.y;
            touchStartRef.current = null;
            if (Math.abs(deltaY) > 120 && Math.abs(deltaY) > Math.abs(deltaX)) {
              onClose();
            }
          }}
          keyboard={{ enabled: true }}
          initialSlide={startIndex}
          slidesPerView={1}
          spaceBetween={24}
          speed={500}
          grabCursor
        >
          {images.map((image, index) => (
            <SwiperSlide key={`${image}-${index}`} className="flex items-center justify-center">
              <img
                src={image}
                alt={`${label || 'Showcase image'} ${index + 1}`}
                className="max-h-[80vh] w-auto max-w-[90vw] select-none object-contain"
                draggable={false}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>,
    document.body
  );
};
